import { type ReactFrontendTool, useFrontendTool as useCopilotKitFrontendTool } from '@copilotkit/react-core/v2'
import { type StandardSchemaV1 } from '@copilotkit/shared'
import { type RefObject, useEffect, useRef } from 'react'
import { type z } from 'zod'
import { zodToJsonSchema } from 'zod-to-json-schema'
import { hasPermission, type Permission } from 'shared/constants/permissions'
import useMember from 'src/workspace/hooks/useMember'
import { ALVIE_COPILOT_AGENT_ID, type CopilotAgentId } from '../copilotSurfaces'
import { waitForDataLoaded } from '../utils/toolDataWait'
import { buildMissingPermissionDescriptionPrefix, buildMissingPermissionToolResult } from '../utils/toolPermission'
import { registerCopilotFrontendToolElementRefs } from './useCopilotFrontendToolElementEffects'

export type CopilotFrontendToolElementRef = RefObject<HTMLElement | null>

export interface AlvieCopilotFrontendToolOptions {
  elementRefs?: CopilotFrontendToolElementRef[]
  /**
   * CopilotKit agent that owns this tool. Defaults to the sidenav page agent so
   * drawer/page tools are not registered as global and inherited by Alvie AI Pro.
   */
  agentId?: CopilotAgentId
}

type CopilotFrontendToolAvailability = boolean | 'enabled' | 'disabled'
type CopilotFrontendToolHandlerContext = Parameters<NonNullable<ReactFrontendTool['handler']>>[1]
type CopilotFrontendToolHandler<T extends Record<string, any>> = (
  args: T,
  context: CopilotFrontendToolHandlerContext
) => unknown
type StandardZodSchema<T extends z.ZodTypeAny> = StandardSchemaV1<z.input<T>, z.output<T>> & {
  toJSONSchema: () => Record<string, unknown>
}

export type CopilotFrontendToolArgs<T extends Record<string, any> = Record<string, unknown>> = Omit<
  ReactFrontendTool,
  'available' | 'handler' | 'parameters'
> &
  AlvieCopilotFrontendToolOptions & {
    available?: CopilotFrontendToolAvailability
    /**
     * Permission from `shared/constants/permissions` the user's role must be granted to run this
     * tool. Required so every tool states it explicitly.
     */
    requiredPermission: Permission
    handler?: CopilotFrontendToolHandler<T>
    parameters?: StandardSchemaV1<any, T>
  }

export type CopilotFrontendToolFn = <T extends Record<string, any> = Record<string, unknown>>(
  tool: CopilotFrontendToolArgs<T>,
  dependencies?: readonly unknown[]
) => void

const toCopilotKitAvailability = (available: CopilotFrontendToolAvailability | undefined): boolean | undefined => {
  if (available === 'disabled') return false
  if (available === 'enabled') return true
  return available
}

const isRefObject = (value: unknown): value is RefObject<unknown> => {
  return typeof value === 'object' && value != null && 'current' in value
}

const isElement = (value: unknown): value is Element => {
  return typeof Element !== 'undefined' && value instanceof Element
}

const toCopilotKitDependencyToken = (dependency: unknown): string => {
  if (dependency == null) return String(dependency)

  if (typeof dependency === 'string' || typeof dependency === 'number' || typeof dependency === 'boolean') {
    return `${typeof dependency}:${String(dependency)}`
  }

  if (typeof dependency === 'bigint') {
    return `bigint:${dependency.toString()}`
  }

  if (typeof dependency === 'function') {
    return `function:${dependency.name}`
  }

  if (isElement(dependency)) {
    return `element:${dependency.tagName}`
  }

  if (isRefObject(dependency)) {
    return isElement(dependency.current) ? `ref:${dependency.current.tagName}` : 'ref'
  }

  const seen = new WeakSet<object>()
  try {
    return (
      JSON.stringify(dependency, (_key, value: unknown) => {
        if (typeof value === 'bigint') {
          return value.toString()
        }

        if (typeof value === 'function') {
          return `[Function:${value.name}]`
        }

        if (isElement(value)) {
          return `[Element:${value.tagName}]`
        }

        if (typeof value === 'object' && value != null) {
          if (seen.has(value)) {
            return '[Circular]'
          }
          seen.add(value)
        }

        return value
      }) ?? 'undefined'
    )
  } catch {
    return Object.prototype.toString.call(dependency)
  }
}

export const zodToolSchema = <T extends z.ZodTypeAny>(schema: T): StandardZodSchema<T> => ({
  '~standard': {
    version: 1,
    vendor: 'zod',
    validate: (value) => {
      const result = schema.safeParse(value)
      if (result.success) {
        return { value: result.data }
      }

      return {
        issues: result.error.issues.map((issue) => ({
          message: issue.message,
          path: issue.path
        }))
      }
    }
  },
  toJSONSchema: () => zodToJsonSchema(schema, { $refStrategy: 'none' }) as Record<string, unknown>
})

/**
 * Wraps CopilotKit's `useFrontendTool` with Alvie-specific frontend tool behavior.
 *
 * Registers element refs for highlights, wraps handlers with `waitForDataLoaded` so tools retry
 * until page data is ready, and enforces `requiredPermission` against the current workspace role.
 *
 * The permission check is a product ceiling, not a security boundary — the API remains the authority.
 */
export const useCopilotFrontendTool: CopilotFrontendToolFn = <T extends Record<string, any> = Record<string, unknown>>(
  tool: CopilotFrontendToolArgs<T>,
  dependencies?: readonly unknown[]
) => {
  const {
    elementRefs,
    available,
    requiredPermission,
    handler,
    agentId = ALVIE_COPILOT_AGENT_ID,
    ...copilotToolBase
  } = tool
  const copilotKitDependencies = dependencies?.map(toCopilotKitDependencyToken)

  const { role: memberRole } = useMember()
  const isPermitted = hasPermission({ memberRole, permission: requiredPermission })

  /**
   * `elementRefs` is intentionally omitted from the effect dependency array.
   * Include any values that change ref identity in the hook's `dependencies` argument.
   */
  useEffect(
    () => {
      if (elementRefs == null) return

      return registerCopilotFrontendToolElementRefs(tool.name, elementRefs)
    },
    // Keep this registry aligned with the caller's CopilotKit tool dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tool.name, ...(dependencies ?? [])]
  )

  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const copilotTool: ReactFrontendTool = {
    ...copilotToolBase,
    description: isPermitted
      ? copilotToolBase.description
      : `${buildMissingPermissionDescriptionPrefix({ memberRole, permission: requiredPermission })} ${
          copilotToolBase.description ?? ''
        }`.trim(),
    agentId,
    available: toCopilotKitAvailability(available)
  }

  if (handler != null) {
    copilotTool.handler = async (args, context) => {
      if (!isPermitted) {
        return buildMissingPermissionToolResult({ memberRole, permission: requiredPermission })
      }

      return await waitForDataLoaded(() => {
        const currentHandler = handlerRef.current
        if (currentHandler == null) {
          throw new Error(`Copilot tool "${tool.name}" handler is not available.`)
        }

        return currentHandler(args as T, context)
      })
    }
  }

  // Re-register when the role changes so the description reflects the current permission.
  const copilotKitDependenciesWithPermission = [
    ...(copilotKitDependencies ?? []),
    `permission:${requiredPermission}:${memberRole}`
  ]

  useCopilotKitFrontendTool(copilotTool, copilotKitDependenciesWithPermission)
}
